import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Plus, X, Search, Map, Flag, GitCompare, TrendingUp, BookOpen, Mail, ChevronRight } from "lucide-react";
import { CONTACT_EMAIL } from "@/lib/api";

const NAV = [
  { to: "/search", label: "Search HOAs", icon: Search },
  { to: "/map", label: "Map View", icon: Map },
  { to: "/states", label: "Browse States", icon: Flag },
  { to: "/compare", label: "Compare", icon: GitCompare },
  { to: "/fee-trends", label: "Fee Trends", icon: TrendingUp },
  { to: "/articles", label: "Articles", icon: BookOpen },
];

const linkCls = ({ isActive }) =>
  `px-3 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-[#E6F4F1] text-[#0B2150]" : "text-[#334155] hover:text-[#0B2150] hover:bg-slate-100"}`;

const mobileCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-3 py-3 text-[15px] font-medium transition-colors ${isActive ? "bg-[#E6F4F1] text-[#0B2150]" : "text-[#334155] active:bg-slate-100"}`;

const MobileMenu = ({ onClose }) => (
  <div className="lg:hidden fixed inset-x-0 top-14 bottom-0 z-40" data-testid="mobile-menu">
    <button className="absolute inset-0 bg-[#061330]/40 backdrop-blur-sm" aria-label="Close menu" onClick={onClose} data-testid="mobile-menu-overlay" />
    <nav aria-label="Mobile" className="relative bg-white border-b border-[#E2E8F0] shadow-xl px-4 pt-3 pb-5 max-h-full overflow-y-auto menu-drop">
      <ul className="grid grid-cols-1 gap-0.5">
        {NAV.map((n) => (
          <li key={n.to}>
            <NavLink to={n.to} onClick={onClose} className={mobileCls} data-testid={`mobile-nav-${n.to.slice(1)}-link`}>
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#F1F5F9] text-[#027A6D]"><n.icon className="h-[18px] w-[18px]" aria-hidden /></span>
              <span className="flex-1">{n.label}</span>
              <ChevronRight className="h-4 w-4 text-slate-400" aria-hidden />
            </NavLink>
          </li>
        ))}
      </ul>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Link to="/submit-hoa" onClick={onClose} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0B2150] px-4 py-3 text-sm font-semibold text-white" data-testid="mobile-nav-submit-hoa-link">
          <Plus className="h-4 w-4" aria-hidden />Add an HOA
        </Link>
        <Link to="/contact" onClick={onClose} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#E2E8F0] px-4 py-3 text-sm font-semibold text-[#0B2150]" data-testid="mobile-nav-contact-link">
          <Mail className="h-4 w-4" aria-hidden />Contact
        </Link>
      </div>
      <a href={`mailto:${CONTACT_EMAIL}`} className="mt-4 block text-center text-xs text-slate-500" data-testid="mobile-nav-email-link">{CONTACT_EMAIL}</a>
    </nav>
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <>
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 lg:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-brand-logo" aria-label="Explore HOA's home" className="shrink-0">
          <img src="/logo.png" alt="Explore HOA's" className="h-8 lg:h-12 w-auto" />
        </Link>
        <nav aria-label="Main" className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.to.slice(1)}-link`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-1.5">
          <Link to="/search" aria-label="Search HOAs" data-testid="mobile-search-btn" className="lg:hidden grid h-10 w-10 place-items-center rounded-full text-[#0B2150] hover:bg-slate-100">
            <Search className="h-5 w-5" />
          </Link>
          <Link to="/submit-hoa" data-testid="nav-submit-hoa-btn"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#0B2150] text-white px-4 py-2 text-sm font-semibold hover:bg-[#03A896] transition-colors">
            <Plus className="h-4 w-4" aria-hidden /> Add an HOA
          </Link>
          <button onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={open ? "Close menu" : "Open menu"} data-testid="mobile-menu-btn"
            className="lg:hidden grid h-10 w-10 place-items-center rounded-full bg-[#F1F5F9] text-[#0B2150] hover:bg-[#E6F4F1] transition-colors">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </header>
    {open && <MobileMenu onClose={() => setOpen(false)} />}
    </>
  );
};
